const tamaguiTarget =
  typeof process !== 'undefined' && typeof process.env === 'object' ? process.env.TAMAGUI_TARGET : undefined;

const isWebTarget =
  tamaguiTarget === 'web' ||
  (typeof document !== 'undefined' && typeof window !== 'undefined' && typeof navigator !== 'undefined');

export const kindredFontFamilies = {
  display: isWebTarget ? '__kindred_display__' : 'Manrope_600SemiBold',
  displayBold: isWebTarget ? '__kindred_display_bold__' : 'Manrope_700Bold',
  sans: isWebTarget ? 'Manrope' : 'Manrope_400Regular',
  sansMedium: isWebTarget ? '__kindred_sans_medium__' : 'Manrope_500Medium',
  sansSemi: isWebTarget ? '__kindred_sans_semi__' : 'Manrope_600SemiBold',
  sansBold: isWebTarget ? '__kindred_sans_bold__' : 'Manrope_700Bold',
  mono: isWebTarget
    ? 'SFMono-Regular, ui-monospace, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
    : 'SpaceMono',
} as const;

export const kindredColorTokens = {
  background: '#F6F7FA',
  canvas: '#F6F7FA',
  canvasMuted: '#EEF1F5',
  foreground: '#1F2127',
  heading: '#6B3566',
  card: '#FFFFFF',
  cardForeground: '#1F2127',
  muted: '#EFF2F6',
  mutedForeground: '#676C78',
  primary: '#6B3566',
  primaryForeground: '#FFFFFF',
  primarySoft: '#F4EDF4',
  secondary: '#5C6B47',
  secondaryForeground: '#FFFFFF',
  secondarySoft: '#EEF4EF',
  accent: '#C8922A',
  accentForeground: '#1F2127',
  accentSoft: '#FBF4E8',
  success: '#2E9E6A',
  successForeground: '#FFFFFF',
  destructive: '#B93852',
  destructiveForeground: '#FFFFFF',
  border: '#DCE2E8',
  borderStrong: '#D4DBE4',
  input: '#D8DFE7',
  inputSoft: '#FBFCFE',
  shadow: 'rgba(24, 31, 43, 0.1)',
  shadowStrong: 'rgba(24, 31, 43, 0.16)',
  overlay: 'rgba(24, 31, 43, 0.18)',
  panel: 'rgba(255, 255, 255, 0.92)',
  panelSoft: 'rgba(249, 251, 253, 0.94)',
  tabBar: 'rgba(255, 255, 255, 0.9)',
} as const;

export const kindredSpaceTokens = {
  0: 0,
  xs: 8,
  sm: 12,
  md: 16,
  lg: 20,
  xl: 24,
  xxl: 32,
  xxxl: 40,
  true: 16,
} as const;

export const kindredSizeTokens = {
  0: 0,
  xs: 12,
  sm: 13,
  md: 15,
  lg: 18,
  xl: 20,
  xxl: 24,
  display: 29,
  hero: 34,
  heroLg: 36,
  true: 15,
} as const;

export const kindredRadiusTokens = {
  0: 0,
  xs: 6,
  sm: 12,
  md: 18,
  lg: 26,
  xl: 34,
  pill: 999,
  true: 18,
} as const;

export const kindredLineHeightTokens = {
  0: 0,
  xs: 16,
  sm: 18,
  md: 22,
  lg: 24,
  xl: 28,
  xxl: 30,
  display: 31,
  hero: 38,
  heroLg: 42,
  true: 22,
} as const;

export const kindredBodyWeightTokens = {
  0: '400',
  xs: '400',
  sm: '500',
  md: '500',
  lg: '600',
  xl: '700',
  xxl: '700',
  display: '600',
  hero: '600',
  heroLg: '700',
  true: '400',
} as const;

export const kindredHeadingWeightTokens = {
  0: '600',
  xs: '600',
  sm: '600',
  md: '600',
  lg: '600',
  xl: '700',
  xxl: '700',
  display: '600',
  hero: '700',
  heroLg: '700',
  true: '600',
} as const;

export const kindredLetterSpacingTokens = {
  0: 0,
  xs: 0,
  sm: -0.03,
  md: -0.06,
  lg: -0.1,
  xl: -0.14,
  xxl: -0.16,
  display: -0.18,
  hero: -0.24,
  heroLg: -0.3,
  true: 0,
} as const;

export const kindredZIndexTokens = {
  0: 0,
  base: 1,
  sticky: 20,
  header: 30,
  overlay: 40,
  tabbar: 50,
  modal: 70,
  toast: 80,
  true: 1,
} as const;

export const kindredNativeColors = {
  canvas: kindredColorTokens.canvas,
  canvasMuted: kindredColorTokens.canvasMuted,
  surface: kindredColorTokens.card,
  surfaceMuted: kindredColorTokens.canvasMuted,
  panel: kindredColorTokens.panel,
  panelSoft: kindredColorTokens.panelSoft,
  tabBar: kindredColorTokens.tabBar,
  border: kindredColorTokens.border,
  borderStrong: kindredColorTokens.borderStrong,
  text: kindredColorTokens.foreground,
  textMuted: kindredColorTokens.mutedForeground,
  heading: kindredColorTokens.heading,
  primary: kindredColorTokens.primary,
  primarySoft: kindredColorTokens.primarySoft,
  secondary: kindredColorTokens.secondary,
  secondaryDeep: kindredColorTokens.secondary,
  secondarySoft: kindredColorTokens.secondarySoft,
  accent: kindredColorTokens.accent,
  accentSoft: kindredColorTokens.accentSoft,
  accentGlow: 'rgba(200, 146, 42, 0.16)',
  primaryGlow: 'rgba(107, 53, 102, 0.12)',
  canvasOnDark: 'rgba(246, 247, 250, 0.9)',
  success: kindredColorTokens.success,
  danger: kindredColorTokens.destructive,
  white: '#FFFFFF',
} as const;

export const kindredNativeSpacing = {
  xs: kindredSpaceTokens.xs,
  sm: kindredSpaceTokens.sm,
  md: kindredSpaceTokens.md,
  lg: kindredSpaceTokens.lg,
  xl: kindredSpaceTokens.xl,
  xxl: kindredSpaceTokens.xxl,
} as const;

export const kindredNativeRadius = {
  pill: kindredRadiusTokens.pill,
  input: kindredRadiusTokens.md,
  card: kindredRadiusTokens.lg,
  panel: kindredRadiusTokens.xl,
} as const;
